import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { VisibilityOffRounded as VisibilityOffRoundedIcon, VisibilityRounded as VisibilityRoundedIcon } from "@mui/icons-material";
import BrandWordmark from "../UI/BrandWordmark";
import colors from "../../assets/theme/colors";
import { auth, db } from "../../services/firebase";
import { createUserProfile } from "../../services/userService";
import { handlePostAuthRedirect } from "../../services/auth/navigation";
import { getGoogleAuthErrorMessage } from "../../services/auth/googleAuthErrors";
import { isE2EMockEnabled, setE2EMockAuthUser } from "../../services/e2eMock";
import { trackEvent } from "../../services/analytics";

const GoogleLogo = () => (
  <Box
    component="svg"
    viewBox="0 0 48 48"
    aria-hidden="true"
    sx={{ width: 20, height: 20, flexShrink: 0, display: "block" }}
  >
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.73 1.22 9.25 3.61l6.91-6.91C35.96 2.76 30.48 0 24 0 14.61 0 6.42 5.38 2.52 13.16l8.04 6.23C12.48 13.1 17.73 9.5 24 9.5z" />
    <path fill="#FBBC05" d="M46.5 24.5c0-1.52-.15-2.98-.43-4.38H24v8.28h12.68c-.55 2.98-2.23 5.51-4.78 7.21l7.36 5.72C43.98 36.4 46.5 30.96 46.5 24.5z" />
    <path fill="#34A853" d="M12.56 28.62c-1.05-3.09-1.05-6.15 0-9.24l-8.04-6.23C1.83 17.41.5 20.62.5 24s1.33 6.59 4.02 10.85l8.04-6.23z" />
    <path fill="#4285F4" d="M24 48c6.48 0 11.96-2.15 15.94-5.84l-7.36-5.72C30.53 37.93 27.48 39 24 39c-6.27 0-11.52-3.6-14.17-8.87l-8.04 6.23C5.42 42.62 14.61 48 24 48z" />
  </Box>
);

const getInitials = (value = "") => {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }

  const base = parts[0] || value.split("@")[0] || "";
  return base.slice(0, 2).toUpperCase();
};

const persistLastUser = (user) => {
  if (typeof window === "undefined" || !user) return;

  const name = user.displayName || user.email || "Caregiver";
  window.localStorage.setItem(
    "ll_last_user",
    JSON.stringify({
      name,
      initials: getInitials(name),
      lastSeen: Date.now(),
    })
  );
};

const getEmailAuthErrorMessage = (error, isSignUpMode) => {
  switch (error?.code) {
    case "auth/email-already-in-use":
      return "The email address is already in use by another account.";
    case "auth/invalid-email":
      return "The email address is not valid.";
    case "auth/weak-password":
      return "The password is too weak. Please use at least 6 characters.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/user-disabled":
      return "This user account has been disabled.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/operation-not-allowed":
      return "Email sign-up is not enabled right now. Please contact support.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a few minutes and try again.";
    case "permission-denied":
      return "Your account was created, but we could not finish setting up your profile. Please contact support.";
    default:
      return isSignUpMode
        ? "Failed to create your account. This is not country-restricted, so please try again or contact support."
        : "Failed to log in. Please try again.";
  }
};

const trackAuthEvent = (eventName, params) => {
  trackEvent(eventName, params);
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 52,
    borderRadius: 3.5,
    bgcolor: colors.semantic.surface,
    transition: "border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
    "& fieldset": {
      borderColor: colors.semantic.line,
      borderWidth: 1.5,
    },
    "&:hover fieldset": {
      borderColor: colors.landing.borderMedium,
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.semantic.sageDeep,
    },
  },
  "& .MuiOutlinedInput-input": {
    px: 2,
    py: 1.5,
    fontSize: 15,
    color: colors.landing.heroText,
    "::placeholder": {
      color: colors.landing.textMuted,
      opacity: 1,
    },
  },
};

const AuthScreen = ({ initialMode = "signin" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, authLoading] = useAuthState(auth);

  const normalizedInitialMode = useMemo(() => {
    if (initialMode === "signup" || initialMode === "reset") return initialMode;
    return "signin";
  }, [initialMode]);

  const [mode, setMode] = useState(normalizedInitialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const isResetMode = normalizedInitialMode === "reset";
  const isSignUpMode = !isResetMode && mode === "signup";
  const isSignInMode = !isResetMode && mode === "signin";

  useEffect(() => {
    setMode(normalizedInitialMode);
  }, [normalizedInitialMode, location.pathname]);

  useEffect(() => {
    if (currentUser && !authLoading) {
      handlePostAuthRedirect(navigate, location, currentUser);
    }
  }, [currentUser, authLoading, navigate, location]);

  useEffect(() => {
    const prefilledEmail =
      location.state?.email ||
      new URLSearchParams(location.search).get("email") ||
      "";

    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [location.search, location.state?.email]);

  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      const timer = window.setTimeout(() => {
        setSuccess("");
        navigate(location.pathname, { replace: true, state: {} });
      }, 5000);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [location.pathname, location.state?.message, navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    trackAuthEvent(isSignUpMode ? "sign_up_started" : "login_started", {
      method: "google",
      mode: isSignUpMode ? "signup" : "signin",
    });

    try {
      if (isE2EMockEnabled()) {
        const mockUser = {
          uid: "cypress-owner",
          email: email || "test@example.com",
          displayName: fullName || "Test Care Owner",
        };
        setE2EMockAuthUser(mockUser);
        persistLastUser(mockUser);
        handlePostAuthRedirect(navigate, location, mockUser);
        return;
      }

      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        await setDoc(userRef, {
          displayName: user.displayName,
          email: user.email,
          role: "care_owner",
          createdAt: new Date(),
        });
      } else {
        await setDoc(
          userRef,
          {
            displayName: user.displayName,
            email: user.email,
          },
          { merge: true }
        );
      }

      persistLastUser(user);
      trackAuthEvent(isSignUpMode ? "sign_up_success" : "login_success", {
        method: "google",
        mode: isSignUpMode ? "signup" : "signin",
      });
      handlePostAuthRedirect(navigate, location, user);
    } catch (err) {
      console.error("Google auth error:", err);
      trackAuthEvent(isSignUpMode ? "sign_up_failed" : "login_failed", {
        method: "google",
        mode: isSignUpMode ? "signup" : "signin",
        error_code: err?.code || "unknown",
      });
      setError(getGoogleAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError("Enter your email address.");
      return;
    }

    if (isResetMode) {
      try {
        setLoading(true);
        await sendPasswordResetEmail(auth, trimmedEmail);
        setSuccess("Check your email for a password reset link.");
      } catch (err) {
        console.error("Password reset error:", err);
        setError("We couldn't send the reset link. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (isSignUpMode && !fullName.trim()) {
      setError("Enter your name.");
      return;
    }

    try {
      setLoading(true);
      trackAuthEvent(isSignUpMode ? "sign_up_started" : "login_started", {
        method: "email",
        mode: isSignUpMode ? "signup" : "signin",
      });

      if (isE2EMockEnabled()) {
        const mockUser = {
          uid: "cypress-owner",
          email: trimmedEmail,
          displayName: isSignUpMode ? fullName.trim() : "Test Care Owner",
        };
        setE2EMockAuthUser(mockUser);
        persistLastUser(mockUser);
        handlePostAuthRedirect(navigate, location, mockUser);
        return;
      }

      if (isSignUpMode) {
        const result = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
        const user = result.user;
        await updateProfile(user, { displayName: fullName.trim() });
        await createUserProfile(user.uid, {
          displayName: fullName.trim(),
          name: fullName.trim(),
          email: trimmedEmail,
          photoURL: user.photoURL,
        });
        try {
          await user.sendEmailVerification();
        } catch (verificationError) {
          console.warn("Email verification could not be sent:", verificationError);
        }
        persistLastUser(user);
        trackAuthEvent("sign_up_success", {
          method: "email",
          mode: "signup",
        });
        handlePostAuthRedirect(navigate, location, user);
        return;
      }

      const result = await signInWithEmailAndPassword(auth, trimmedEmail, password);
      persistLastUser(result.user);
      trackAuthEvent("login_success", {
        method: "email",
        mode: "signin",
      });
      handlePostAuthRedirect(navigate, location, result.user);
    } catch (err) {
      console.error("Email auth error:", err);
      trackAuthEvent(isSignUpMode ? "sign_up_failed" : "login_failed", {
        method: "email",
        mode: isSignUpMode ? "signup" : "signin",
        error_code: err?.code || "unknown",
      });
      setError(getEmailAuthErrorMessage(err, isSignUpMode));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: colors.auth.pageBg,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
      sx={{
          background: `linear-gradient(180deg, ${colors.auth.heroGradientStart} 0%, ${colors.auth.heroGradientEnd} 100%)`,
          pt: { xs: 4.5, sm: 5.5, md: 6.5 },
          pb: { xs: 4, sm: 4.5, md: 5 },
          px: 2.5,
          textAlign: "center",
        }}
      >
        <Box sx={{ maxWidth: 680, mx: "auto" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.4 }}>
            <BrandWordmark variant="minimal" showIcon={false} color={colors.landing.heroText} />
          </Box>
          <Typography
            sx={{
              fontSize: { xs: "1.2rem", sm: "1.3rem", md: "1.4rem" },
              lineHeight: 1.35,
              color: colors.landing.midNavy,
              letterSpacing: "-0.02em",
              maxWidth: 500,
              mx: "auto",
            }}
          >
            The full picture, for every caregiver.
          </Typography>
          <Box sx={{ mt: 2.6, display: "flex", justifyContent: "center", gap: 1.2 }}>
            {[
              colors.semantic.meds.soft,
              colors.semantic.categories.sleep.bg,
              colors.semantic.categories.food.bg,
              colors.auth.dotLavender,
              colors.semantic.categories.behavior.bg,
            ].map((dotColor) => (
              <Box
                key={dotColor}
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  bgcolor: dotColor,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 3 },
          pb: { xs: 4, sm: 6 },
          pt: 0,
          bgcolor: colors.landing.pageBackground,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 560,
            mx: "auto",
            mt: { xs: -3, sm: -4 },
            p: { xs: 2.5, sm: 4 },
            borderRadius: "24px",
            bgcolor: colors.semantic.surface,
            border: `1px solid ${colors.semantic.sageSoft}`,
            boxShadow: "0 18px 44px rgba(20, 40, 30, 0.08)",
          }}
        >
          {!isResetMode ? (
            <Box
              sx={{
                mb: 3.5,
                p: 0.5,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.5,
                borderRadius: 3,
                border: `1.5px solid ${colors.semantic.line}`,
                bgcolor: colors.semantic.surface,
              }}
            >
              <Button
                type="button"
                variant="contained"
                disableElevation
                onClick={() => setMode("signin")}
                sx={{
                  height: 48,
                  minWidth: 0,
                  borderRadius: 1.75,
                  textTransform: "none",
                  fontSize: "0.96rem",
                  fontWeight: 700,
                  backgroundColor: isSignInMode ? colors.auth.ctaPrimary : colors.semantic.surface,
                  color: isSignInMode ? colors.semantic.surface : colors.semantic.ink,
                  border: isSignInMode ? "none" : `1px solid ${colors.semantic.line}`,
                  boxShadow: isSignInMode ? `0 6px 16px ${alpha(colors.auth.ctaPrimary, 0.24)}` : "none",
                  opacity: 1,
                  "&:hover": {
                    backgroundColor: isSignInMode ? colors.auth.ctaPrimaryHover : colors.landing.sageLight,
                  },
                }}
              >
                Sign in
              </Button>
              <Button
                type="button"
                variant="contained"
                disableElevation
                onClick={() => setMode("signup")}
                sx={{
                  height: 48,
                  minWidth: 0,
                  borderRadius: 2.75,
                  textTransform: "none",
                  fontSize: "0.96rem",
                  fontWeight: 700,
                  backgroundColor: isSignUpMode ? colors.semantic.sageDeep : colors.semantic.surface,
                  color: isSignUpMode ? colors.semantic.surface : colors.semantic.ink,
                  border: "none",
                  boxShadow: isSignUpMode ? `0 6px 16px ${alpha(colors.semantic.sageDeep, 0.22)}` : "none",
                  opacity: 1,
                  "&:hover": {
                    backgroundColor: isSignUpMode ? colors.auth.signUpActiveHover : colors.landing.sageLight,
                  },
                }}
              >
                Get started
              </Button>
            </Box>
          ) : null}

          {!isResetMode ? (
            <>
              <Button
                type="button"
                fullWidth
                onClick={handleGoogleLogin}
                disabled={loading}
                startIcon={<GoogleLogo />}
                sx={{
                  height: 52,
                  borderRadius: 3,
                  bgcolor: colors.semantic.surface,
                  border: `1.5px solid ${colors.semantic.line}`,
                  boxShadow: "0 1px 2px rgba(20,40,30,0.04)",
                  mb: 2.5,
                  color: colors.landing.heroText,
                  textTransform: "none",
                  fontSize: "0.97rem",
                  fontWeight: 650,
                  letterSpacing: "-0.01em",
                  whiteSpace: "nowrap",
                  gap: 1.25,
                  "&:hover": {
                    bgcolor: colors.auth.googleButtonHoverBg,
                    borderColor: colors.landing.borderMedium,
                    boxShadow: "0 4px 10px rgba(20,40,30,0.06)",
                  },
                }}
              >
                Continue with Google
              </Button>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                <Divider sx={{ flex: 1, borderColor: colors.semantic.line }} />
                <Typography sx={{ fontSize: "0.82rem", color: colors.semantic.ink2, fontWeight: 600, whiteSpace: "nowrap" }}>
                  or use email
                </Typography>
                <Divider sx={{ flex: 1, borderColor: colors.semantic.line }} />
              </Box>
            </>
          ) : (
            <Typography sx={{ mb: 2.5, color: colors.semantic.ink2, fontSize: "0.95rem", lineHeight: 1.55, textAlign: "center" }}>
              Enter your email address and we&apos;ll send you a password reset link.
            </Typography>
          )}

          {isSignUpMode ? (
            <Typography sx={{ mb: 2, color: colors.semantic.ink2, fontSize: "0.9rem", lineHeight: 1.5, textAlign: "center" }}>
              Sign up with Google, or use your name, email, and password.
            </Typography>
          ) : null}

          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={handleEmailSubmit} noValidate>
            {isSignUpMode ? (
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ mb: 0.75, fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.02em", color: colors.semantic.ink }}>
                  Name
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Your name"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  autoComplete="name"
                  sx={fieldSx}
                />
              </Box>
            ) : null}

            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ mb: 0.75, fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.02em", color: colors.semantic.ink }}>
                Email address
              </Typography>
              <TextField
                fullWidth
                placeholder="you@example.com"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                autoFocus
                sx={fieldSx}
              />
            </Box>

            {!isResetMode ? (
              <Box sx={{ mb: isSignInMode ? 3 : 3.2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 0.75 }}>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.02em", color: colors.semantic.ink }}>
                    Password
                  </Typography>
                  {isSignInMode ? (
                    <Link
                      component={RouterLink}
                      to="/forgot-password"
                      underline="none"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 650,
                        color: colors.semantic.ink,
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                      }}
                    >
                      Forgot password?
                    </Link>
                  ) : null}
                </Box>
                <TextField
                  fullWidth
                  placeholder="••••••••"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete={isSignUpMode ? "new-password" : "current-password"}
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((value) => !value)}
                          edge="end"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          sx={{ color: colors.semantic.ink2 }}
                        >
                          {showPassword ? (
                            <VisibilityOffRoundedIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            ) : null}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disableElevation
              disabled={loading}
              sx={{
                height: 54,
                minWidth: 0,
                borderRadius: 3,
                backgroundColor: colors.auth.ctaPrimary,
                color: colors.semantic.surface,
                fontSize: "1rem",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                boxShadow: `0 6px 20px ${alpha(colors.auth.ctaPrimary, 0.34)}`,
                mb: 3,
                textTransform: "none",
                opacity: 1,
                "&:hover": {
                  backgroundColor: colors.auth.ctaPrimaryHover,
                  boxShadow: `0 8px 24px ${alpha(colors.auth.ctaPrimary, 0.38)}`,
                },
                "&.Mui-disabled": {
                  backgroundColor: colors.auth.ctaPrimary,
                  color: colors.semantic.surface,
                  opacity: 0.92,
                },
              }}
              endIcon={<Box component="span" sx={{ color: alpha(colors.semantic.surface, 0.8), fontSize: "1.15rem", lineHeight: 1 }}>›</Box>}
            >
              {isResetMode ? "Send reset link" : isSignUpMode ? "Create account" : "Sign in"}
            </Button>

            {isResetMode ? (
              <Box sx={{ textAlign: "center" }}>
                <Link
                  component={RouterLink}
                  to="/login"
                  underline="none"
                  sx={{
                    color: colors.semantic.ink2,
                    fontSize: "0.88rem",
                    fontWeight: 600,
                  }}
                >
                  Back to sign in →
                </Link>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center" }}>
                {isSignInMode ? (
                  <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.88rem" }}>
                    Don&apos;t have an account?{" "}
                    <Button
                      type="button"
                      onClick={() => setMode("signup")}
                      sx={{
                        minWidth: "auto",
                        p: 0,
                        textTransform: "none",
                        fontSize: "0.88rem",
                        fontWeight: 800,
                        color: colors.semantic.ink,
                        verticalAlign: "baseline",
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                      }}
                    >
                      Get started →
                    </Button>
                  </Typography>
                ) : (
                  <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.88rem" }}>
                    Already have an account?{" "}
                    <Button
                      type="button"
                      onClick={() => setMode("signin")}
                      sx={{
                        minWidth: "auto",
                        p: 0,
                        textTransform: "none",
                        fontSize: "0.88rem",
                        fontWeight: 800,
                        color: colors.semantic.ink,
                        verticalAlign: "baseline",
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                      }}
                    >
                      Sign in →
                    </Button>
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Typography
            sx={{
              mt: 3.5,
              textAlign: "center",
              fontSize: "0.74rem",
              lineHeight: 1.5,
              color: colors.semantic.ink2,
            }}
          >
            By continuing you agree to our{" "}
            <Link component={RouterLink} to="/terms-of-service" underline="none" sx={{ color: colors.semantic.sageDeep, fontWeight: 600 }}>
              Terms
            </Link>{" "}
            and{" "}
            <Link component={RouterLink} to="/privacy-policy" underline="none" sx={{ color: colors.semantic.sageDeep, fontWeight: 600 }}>
              Privacy Policy
            </Link>
            .
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default AuthScreen;
